import type { LucideIcon } from "lucide-react";
import { AlertTriangle, Info, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib/utils";

/** Callout intent — selects the icon, accent color, and default eyebrow label. */
export type CalloutVariant = "note" | "important" | "warning";

interface CalloutProps {
	/** Intent of the callout. Defaults to "note". */
	variant?: CalloutVariant;
	/** Optional override for the small eyebrow label (defaults per variant). */
	title?: string;
	children: ReactNode;
	className?: string;
}

interface CalloutStyle {
	icon: LucideIcon;
	label: string;
	/** Left rule + faint tint + icon color. */
	container: string;
	icon_color: string;
}

// Variant → color as SIGNAL. The default "note" wears the brand's warm lavender
// callout surface (the `--accent` tint #F3EFFB) with a purple icon, label, and left
// rule — the resources-hub treatment, NOT the prototype's crimson product style.
// `important` (success mint) and `warning` (amber) keep their semantic colors so a
// reader can still tell an assurance from a caution at a glance. Each uses a left
// rule + faint tint, elevated from a plain blockquote to a labeled, iconified block.
const VARIANT_STYLE: Record<CalloutVariant, CalloutStyle> = {
	note: {
		icon: Info,
		label: "Note",
		container: "border-accent-foreground/15 bg-accent [border-left-color:oklch(var(--primary))]",
		icon_color: "text-primary",
	},
	important: {
		icon: ShieldCheck,
		label: "Important",
		container: "border-success/20 bg-success/[0.05] [border-left-color:oklch(var(--success))]",
		icon_color: "text-success",
	},
	warning: {
		icon: AlertTriangle,
		label: "Heads up",
		container: "border-warning/20 bg-warning/[0.05] [border-left-color:oklch(var(--warning))]",
		icon_color: "text-warning",
	},
};

/**
 * A labeled note/important/warning callout: a lucide icon, a small-caps eyebrow,
 * and the body, set off by a tinted left rule. The kind of emphasis block markdown
 * can only fake with a blockquote — here it's a first-class, color-coded element.
 */
export function Callout({ variant = "note", title, children, className }: CalloutProps) {
	const style = VARIANT_STYLE[variant];
	const Icon = style.icon;

	return (
		<div className={cn("flex gap-3 rounded-2xl border border-l-2 p-4", style.container, className)}>
			<Icon className={cn("mt-0.5 h-5 w-5 shrink-0", style.icon_color)} aria-hidden="true" />
			<div className="min-w-0">
				<div className={cn("text-[11px] font-semibold uppercase tracking-[0.16em]", style.icon_color)}>
					{title ?? style.label}
				</div>
				<div className="mt-1.5 text-[15px] leading-relaxed text-foreground/90 [&_strong]:font-semibold [&_strong]:text-foreground">
					{children}
				</div>
			</div>
		</div>
	);
}
